<template lang="pug">
h5.billing-mode-summary
  span.text-muted-foreground.font-normal {{ expiredPeriod ? '到期前计费模式：' : '当前服务计费模式：' }}
  b.billing-mode-value(
    :class="mode.key === 'anchor' ? 'text-emerald-700 dark:text-emerald-300' : 'text-amber-700 dark:text-amber-300'"
  )
    span.mode-dot(aria-hidden="true")
    | {{ mode.label }}
  Sheet
    SheetTrigger(as-child)
      button.billing-help-trigger(
        type="button"
        aria-label="查看两种计费方式"
        title="查看两种计费方式"
      )
        Icon(name="material-symbols:help-outline-rounded" class="h-4 w-4")
    SheetContent(
      side="right"
      class="w-[min(94vw,780px)] sm:max-w-[780px] overflow-y-auto p-0 gap-0"
    )
      SheetHeader(class="sticky top-0 z-10 border-b bg-background px-5 py-4 pr-12 sm:px-7")
        SheetTitle 两种计费方式有什么不同
        SheetDescription 自然月计费都在每月 1 日换月；按订阅日计费则在自己的购买日换月。

      main.billing-guide
        section.current-mode-strip(:class="`is-${mode.key}`")
          div
            p.section-kicker 当前服务
            h3 {{ mode.label }}
          p.current-cycle-detail {{ currentCycleDetail }}

        section.guide-section
          div.section-heading
            div
              p.section-kicker 续费日
              h3 同一天购买，什么时候换月
            p.section-note 假设 8 月 18 日购买 2 个月

          .timeline-comparison
            article.timeline-lane.legacy-lane(:class="{ 'is-current': mode.key === 'legacy' }")
              header
                span.mode-chip legacy
                strong 自然月
                span.current-tag(v-if="mode.key === 'legacy'") 当前服务
              p 第一个月只算 8 月剩下的天数，以后都在每月 1 日换月。
              .date-axis(aria-label="legacy 自然月时间轴：8月18日购买，9月1日和10月1日为周期边界")
                .axis-line
                span.axis-point.start
                span.axis-point.middle
                span.axis-point.end
                .axis-labels
                  span
                    b 8/18
                    small 购买
                  span
                    b 9/1
                    small 换月
                  span
                    b 10/1
                    small 到期

            article.timeline-lane.anchor-lane(:class="{ 'is-current': mode.key === 'anchor' }")
              header
                span.mode-chip anchor
                strong 按订阅日计费
                span.current-tag(v-if="mode.key === 'anchor'") 当前服务
              p 从 8 月 18 日开始算完整一个月，以后都在每月 18 日换月。
              .date-axis(aria-label="按订阅日计费时间轴：8月18日购买，9月18日和10月18日为周期边界")
                .axis-line
                span.axis-point.start
                span.axis-point.middle
                span.axis-point.end
                .axis-labels
                  span
                    b 8/18
                    small 购买
                  span
                    b 9/18
                    small 续费
                  span
                    b 10/18
                    small 到期

        section.guide-section
          div.section-heading
            div
              p.section-kicker 月底购买
              h3 订阅日在 29、30、31 日怎么办
            p.section-note 原订阅日不会丢

          .edge-case-rule
            Icon(name="material-symbols:calendar-month-outline-rounded" class="h-5 w-5 shrink-0")
            p 当月没有这个日期，就在最后一天续费。下个月有这个日期时，再回到原来的订阅日。

          .edge-case-grid
            article.edge-case-card
              header
                span.calendar-badge 2/29
                div
                  strong 闰年 2 月 29 日购买
                  p 订阅日一直是 29 日
              ol.anchor-sequence(aria-label="闰年2月29日购买后的续费日序列")
                li
                  b 2028/2/29
                  small 购买
                li
                  b 3/29
                  small 正常续费
                li.sequence-gap
                  b …
                  small 每月 29 日
                li.clipped-date
                  b 2029/2/28
                  small 临时截断
                li.restored-date
                  b 3/29
                  small 恢复 29 日
              p.case-explanation 平年 2 月没有 29 日，所以改在 28 日续费；到了 3 月，再回到 29 日。

            article.edge-case-card
              header
                span.calendar-badge 10/31
                div
                  strong 10 月 31 日购买
                  p 订阅日一直是 31 日
              ol.anchor-sequence(aria-label="10月31日购买后一直到次年3月的续费日序列")
                li
                  b 10/31
                  small 购买
                li.clipped-date
                  b 11/30
                  small 临时截断
                li.restored-date
                  b 12/31
                  small 恢复 31 日
                li
                  b 1/31
                  small 正常续费
                li.clipped-date
                  b 2/28（闰年为 2/29）
                  small 临时截断
                li.restored-date
                  b 3/31
                  small 再次恢复
              p.case-explanation 11 月改在 30 日续费，12 月回到 31 日。2 月用最后一天，3 月再回到 31 日。

        section.guide-section
          div.section-heading
            div
              p.section-kicker 第一次购买
              h3 月中购买，付多少钱、拿多少流量
            p.section-note 以 €6 / 月、20 GB 套餐为例

          figure.billing-ratio-chart
            figcaption.sr-only 8 月 18 日购买 €6 每月、20 GB 套餐时，自然月计费按 14 / 31 折算，按订阅日计费按完整一个月计算。
            .ratio-row
              .ratio-label
                span.mode-chip legacy
                b 14 / 31 天
              .ratio-track(role="img" aria-label="legacy 首期计费比例约 45.16%")
                span.ratio-fill.legacy-fill(style="width: 45.16%")
              p
                strong 约 €2.71
                span 价格
                strong 约 9.03 GB
                span 流量
            .ratio-row
              .ratio-label
                span.mode-chip anchor
                b 完整 1 个月
              .ratio-track(role="img" aria-label="按订阅日计费首期比例 100%")
                span.ratio-fill.anchor-fill(style="width: 100%")
              p
                strong €6
                span 价格
                strong 20 GB
                span 流量
          p.ratio-footnote 自然月计费只算这个月还剩多少天；按订阅日计费从购买当天开始算完整一个月。

        section.guide-section
          div.section-heading
            div
              p.section-kicker 套餐升级
              h3 从 €6 / 20 GB 升到 €12 / 500 GB 怎么算
            p.section-note 立即升级，续费日不变

          .upgrade-intro(class="rounded-xl border bg-background p-4")
            p(class="m-0 text-xs leading-6 text-muted-foreground") 只补剩余时间里的价格差和流量差。原来已经买到的流量不会被扣掉。
            .plan-change(
              class="mt-3 flex flex-wrap items-center gap-2 text-sm"
              aria-label="从每月6欧元20GB套餐升级到每月12欧元500GB套餐"
            )
              span(class="rounded-lg bg-muted px-3 py-2") €6 / 月 · 20 GB
              Icon(name="material-symbols:arrow-forward-rounded" class="h-5 w-5")
              strong(class="rounded-lg bg-emerald-50 px-3 py-2 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300") €12 / 月 · 500 GB

          .upgrade-formulas(class="grid grid-cols-1 gap-2 sm:grid-cols-2")
            div(class="grid gap-1 rounded-lg bg-muted p-3 text-xs")
              span.text-muted-foreground 价格差
              strong €12 − €6 = €6 / 月
            div(class="grid gap-1 rounded-lg bg-muted p-3 text-xs")
              span.text-muted-foreground 流量差
              strong 500 GB − 20 GB = 480 GB / 月
            div.formula-wide(class="rounded-lg border border-emerald-200 bg-emerald-50/50 px-3 py-2 text-sm font-semibold sm:col-span-2 dark:border-emerald-800 dark:bg-emerald-950/30")
              code.font-sans 立即收费 = €6 × 剩余比例
            div.formula-wide(class="rounded-lg border border-emerald-200 bg-emerald-50/50 px-3 py-2 text-sm font-semibold sm:col-span-2 dark:border-emerald-800 dark:bg-emerald-950/30")
              code.font-sans 立即增加流量 = 480 GB × 剩余比例
            div.formula-wide(class="rounded-lg border border-emerald-200 bg-emerald-50/50 px-3 py-2 text-sm font-semibold sm:col-span-2 dark:border-emerald-800 dark:bg-emerald-950/30")
              code.font-sans 新总额度 = 原总额度 + 立即增加流量

          .mode-ratio-grid(class="grid grid-cols-1 gap-3 sm:grid-cols-2")
            article.mode-ratio-card(class="grid gap-2 rounded-xl border p-3")
              span.mode-chip legacy
              strong.text-xs 自然月计费看什么比例？
              p(class="m-0 text-xs leading-5 text-muted-foreground") 看本自然月剩余日历天数比例。
            article.mode-ratio-card(class="grid gap-2 rounded-xl border p-3")
              span.mode-chip anchor
              strong.text-xs 按订阅日计费看什么比例？
              p(class="m-0 text-xs leading-5 text-muted-foreground") 看当前订阅周期剩余时间比例。

          .upgrade-example(class="rounded-xl border bg-background p-4")
            div.example-heading(class="mb-3 flex flex-col gap-1 text-xs sm:flex-row sm:items-center sm:justify-between")
              span.text-muted-foreground 举例
              strong 剩余 50%，已经用了 8 GB
            .example-results(class="mb-3 grid grid-cols-1 gap-2 sm:grid-cols-3")
              div(class="grid gap-1 rounded-lg bg-muted p-3 text-xs")
                span.text-muted-foreground 立即收费
                strong.text-base €3
              div(class="grid gap-1 rounded-lg bg-muted p-3 text-xs")
                span.text-muted-foreground 立即增加
                strong.text-base 240 GB
              div(class="grid gap-1 rounded-lg bg-muted p-3 text-xs")
                span.text-muted-foreground 新总额度
                strong.text-base 260 GB
            p(class="m-0 text-xs leading-6 text-muted-foreground") 已用 8 GB，本期还可用 252 GB。下次续费收 €12，流量重置为 500 GB。
          p.ratio-footnote 如果还有预付月份，每个完整月份再补 €6 差价；这些月份每月都有 500 GB。

        section.guide-section
          div.section-heading
            div
              p.section-kicker 操作对照
              h3 买、续、升级时分别会发生什么
            p.section-note 横向滚动可查看完整内容

          .operation-table-wrap
            table.operation-table
              caption.sr-only legacy 自然月计费与 anchor 按订阅日计费的操作差异
              thead
                tr
                  th 操作
                  th
                    span.mode-chip legacy
                    | 自然月
                  th
                    span.mode-chip anchor
                    | 按订阅日计费
              tbody
                tr
                  th 新购 / 过期重购
                  td 只收这个月剩余天数的钱，也只发对应流量；下次从 1 日开始。
                  td 收一个整月的钱，发一个整月流量；下次在购买日续费。
                tr
                  th 续买同套餐
                  td 按所选月数续买，仍在每月 1 日换月。
                  td 按所选月数续买，原来的续费日不变。
                tr
                  th 升级
                  td 用这个月的剩余比例计算差价和新增流量；仍在每月 1 日换月。
                  td 用当前订阅周期的剩余比例计算差价和新增流量；续费日不变。
                tr
                  th 降级
                  td 这个月继续用原套餐，下月 1 日再换到低套餐。
                  td 当前周期继续用原套餐，下个续费日再换到低套餐。
                tr
                  th 自动续费与流量重置
                  td 每月 1 日扣费并重置流量。
                  td 到自己的续费日扣费并重置流量。
                tr
                  th 管理员改月数
                  td 增加的月数都从每月 1 日往后算。
                  td 从当前续费日往后算；需要时可以修改续费日。
                tr
                  th 停止本期
                  td 停到下月 1 日；有预付月就恢复并扣掉 1 个月。
                  td 停到本期结束；有预付月就恢复并扣掉 1 个月。

        section.guide-section.operator-notes
          div.section-heading
            div
              p.section-kicker 最后记住
              h3 看服务本身，不要只看全局开关
          ul
            li 当前服务上写着哪种计费方式，就按哪种方式处理。
            li 已经按订阅日计费的服务，关闭新购开关后也不会变回自然月计费。
            li 月底没有原订阅日时，先用当月最后一天；下个月会自动恢复。
</template>

<script lang="ts" setup>
import type { ServicePeriod } from '~/types/servicePeriod'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'

const props = defineProps<{
  service?: userServicesAttributes | null
}>()

const dayjs = useDayjs()
const { period: expiredPeriod, pending: expiredPeriodPending, error: expiredPeriodError } = useExpiredAnchorPeriod(() => props.service)

function billingModePresentation(serviceValue: userServicesAttributes | null | undefined, endedPeriod: ServicePeriod | null = null) {
  if (isAnchorBilledService(serviceValue)) {
    return {
      key: 'anchor',
      label: 'anchor · 按订阅日计费',
    }
  }
  if (endedPeriod) {
    return {
      key: 'anchor',
      label: 'anchor · 按订阅日计费（已到期）',
    }
  }
  return {
    key: 'legacy',
    label: 'legacy · 自然月计费',
  }
}

const mode = computed(() => {
  if (expiredPeriodPending.value) return { key: 'unknown', label: '读取中…' }
  if (expiredPeriodError.value) return { key: 'unknown', label: '计费记录暂不可用' }
  return billingModePresentation(props.service, expiredPeriod.value)
})

const currentCycleDetail = computed(() => {
  if (expiredPeriodPending.value) return '正在读取最近一期计费记录'
  if (expiredPeriodError.value) return '读取计费记录失败，请刷新后重试'
  if (expiredPeriod.value) {
    const start = dayjs(expiredPeriod.value.period_start).tz('Asia/Shanghai').format('YYYY/MM/DD HH:mm')
    const end = dayjs(expiredPeriod.value.period_end).tz('Asia/Shanghai').format('YYYY/MM/DD HH:mm')
    return `最近一期：${start} 至 ${end} · 已到期`
  }
  if (!isAnchorBilledService(props.service)) {
    return '每月 1 日续费并重置流量'
  }
  const periodEnd = dayjs(props.service?.period_end).tz('Asia/Shanghai')
  const periodStart = props.service?.period_start
    ? dayjs(props.service.period_start).tz('Asia/Shanghai').format('YYYY/MM/DD HH:mm')
    : '起点未记录'
  const anchorDay = props.service?.anchor_day || periodEnd.date()
  return `本期：${periodStart} 至 ${periodEnd.format('YYYY/MM/DD HH:mm')} · 通常在每月 ${anchorDay} 日续费`
})
</script>

<style lang="postcss" scoped>

.billing-mode-summary {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.25rem;
}

.billing-mode-value {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
}

.mode-dot {
  width: 0.45rem;
  height: 0.45rem;
  border-radius: 999px;
  background: currentColor;
}

.billing-help-trigger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.25rem;
  height: 1.25rem;
  margin-left: 0.15rem;
  border-radius: 999px;
  color: var(--muted-foreground);
  transition: color 160ms ease, background-color 160ms ease, transform 160ms ease;
}

.billing-help-trigger:hover {
  color: var(--foreground);
  background: var(--accent);
  transform: translateY(-1px);
}

.billing-help-trigger:focus-visible {
  outline: 2px solid var(--ring);
  outline-offset: 2px;
}

.billing-guide {
  display: grid;
  gap: 2rem;
  padding: 1.5rem 1.25rem 3rem;
}

.guide-kicker,
.section-kicker {
  margin: 0;
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--muted-foreground);
}

.current-mode-strip {
  display: grid;
  grid-template-columns: minmax(0, 0.8fr) minmax(0, 1.2fr);
  gap: 1rem;
  align-items: center;
  padding: 1.1rem 1.2rem;
  border: 1px solid var(--border);
  border-radius: 0.8rem;
}

.current-mode-strip.is-legacy {
  background: color-mix(in oklab, #f59e0b 9%, var(--background));
}

.current-mode-strip.is-anchor {
  background: color-mix(in oklab, #10b981 9%, var(--background));
}

.current-mode-strip h3,
.guide-section h3 {
  margin: 0.15rem 0 0;
  font-size: 1.05rem;
  font-weight: 750;
  letter-spacing: -0.02em;
}

.current-cycle-detail {
  margin: 0;
  font-size: 0.78rem;
  line-height: 1.55;
  color: var(--muted-foreground);
}

.guide-section {
  display: grid;
  gap: 1rem;
}

.section-heading {
  display: flex;
  align-items: end;
  justify-content: space-between;
  gap: 1rem;
}

.section-note {
  margin: 0;
  font-size: 0.74rem;
  color: var(--muted-foreground);
  text-align: right;
}

.timeline-comparison {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
}

.timeline-lane {
  min-width: 0;
  padding: 1rem;
  border: 1px solid var(--border);
  border-radius: 0.8rem;
  background: var(--background);
}

.timeline-lane.is-current {
  border-color: var(--foreground);
  box-shadow: inset 0 0 0 1px var(--foreground);
}

.timeline-lane header {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.45rem;
}

.timeline-lane > p {
  min-height: 2.8rem;
  margin: 0.6rem 0 1.15rem;
  font-size: 0.76rem;
  line-height: 1.5;
  color: var(--muted-foreground);
}

.mode-chip,
.current-tag {
  display: inline-flex;
  align-items: center;
  width: max-content;
  padding: 0.15rem 0.42rem;
  border-radius: 0.3rem;
  font-size: 0.64rem;
  font-weight: 800;
  line-height: 1.25;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  white-space: nowrap;
  background: var(--muted);
  color: var(--muted-foreground);
}

.current-tag {
  margin-left: auto;
  background: var(--foreground);
  color: var(--background);
}

.date-axis {
  position: relative;
  padding-top: 0.3rem;
}

.axis-line {
  position: absolute;
  top: 0.6rem;
  right: 0.2rem;
  left: 0.2rem;
  height: 2px;
  background: var(--border);
}

.axis-point {
  position: absolute;
  top: 0.32rem;
  width: 0.6rem;
  height: 0.6rem;
  border: 2px solid var(--background);
  border-radius: 999px;
  background: var(--muted-foreground);
  box-shadow: 0 0 0 1px var(--muted-foreground);
}

.axis-point.start { left: 0; }
.axis-point.middle { left: 50%; transform: translateX(-50%); }
.axis-point.end { right: 0; }

.legacy-lane .axis-line,
.legacy-lane .axis-point { background: #d97706; }
.anchor-lane .axis-line,
.anchor-lane .axis-point { background: #059669; }

.axis-labels {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  padding-top: 1.15rem;
}

.axis-labels span {
  display: grid;
  gap: 0.05rem;
  font-size: 0.72rem;
}

.axis-labels span:nth-child(2) { text-align: center; }
.axis-labels span:last-child { text-align: right; }
.axis-labels small { color: var(--muted-foreground); }

.edge-case-rule {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  padding: 0.75rem 0.85rem;
  border: 1px solid color-mix(in oklab, #059669 30%, var(--border));
  border-radius: 0.65rem;
  background: color-mix(in oklab, #10b981 7%, var(--background));
  color: var(--foreground);
}

.edge-case-rule p {
  margin: 0;
  font-size: 0.76rem;
  line-height: 1.5;
}

.edge-case-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 0.75rem;
}

.edge-case-card {
  min-width: 0;
  padding: 1rem;
  border: 1px solid var(--border);
  border-radius: 0.8rem;
}

.edge-case-card header {
  display: flex;
  align-items: center;
  gap: 0.7rem;
}

.edge-case-card header > div {
  display: grid;
  gap: 0.1rem;
}

.edge-case-card header p,
.case-explanation {
  margin: 0;
  font-size: 0.7rem;
  line-height: 1.5;
  color: var(--muted-foreground);
}

.calendar-badge {
  display: grid;
  place-items: center;
  flex: 0 0 auto;
  width: 2.8rem;
  height: 2.8rem;
  border-radius: 0.55rem;
  background: var(--foreground);
  color: var(--background);
  font-size: 0.78rem;
  font-weight: 800;
}

.anchor-sequence {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  overflow-x: auto;
  margin: 1rem 0 0.75rem;
  padding: 0 0 0.35rem;
  list-style: none;
}

.anchor-sequence li {
  position: relative;
  display: grid;
  flex: 0 0 auto;
  gap: 0.08rem;
  font-size: 0.68rem;
}

.anchor-sequence li:not(:last-child)::after {
  position: absolute;
  top: 0.05rem;
  right: -0.62rem;
  content: '→';
  color: var(--muted-foreground);
}

.anchor-sequence b {
  font-size: 0.72rem;
}

.anchor-sequence small {
  color: var(--muted-foreground);
  white-space: nowrap;
}

.anchor-sequence .clipped-date b { color: #d97706; }
.anchor-sequence .restored-date b { color: #059669; }

.billing-ratio-chart {
  display: grid;
  gap: 1rem;
  margin: 0;
  padding: 1rem;
  border: 1px solid var(--border);
  border-radius: 0.8rem;
}

.ratio-row {
  display: grid;
  grid-template-columns: 8.5rem minmax(8rem, 1fr) 12rem;
  gap: 0.85rem;
  align-items: center;
}

.ratio-label {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  font-size: 0.74rem;
}

.ratio-track {
  height: 0.65rem;
  overflow: hidden;
  border-radius: 999px;
  background: var(--muted);
}

.ratio-fill {
  display: block;
  height: 100%;
  border-radius: inherit;
}

.legacy-fill { background: #d97706; }
.anchor-fill { background: #059669; }

.ratio-row > p {
  display: grid;
  grid-template-columns: auto 1fr auto 1fr;
  gap: 0.25rem;
  align-items: baseline;
  margin: 0;
  font-size: 0.68rem;
  color: var(--muted-foreground);
}

.ratio-row > p strong {
  font-size: 0.78rem;
  color: var(--foreground);
}

.ratio-footnote {
  margin: -0.35rem 0 0;
  font-size: 0.72rem;
  line-height: 1.5;
  color: var(--muted-foreground);
}

.operation-table-wrap {
  overflow-x: auto;
  border: 1px solid var(--border);
  border-radius: 0.8rem;
}

.operation-table {
  width: 100%;
  min-width: 680px;
  border-collapse: collapse;
  font-size: 0.75rem;
  line-height: 1.5;
}

.operation-table th,
.operation-table td {
  padding: 0.75rem;
  border-bottom: 1px solid var(--border);
  text-align: left;
  vertical-align: top;
}

.operation-table thead th {
  background: var(--muted);
  font-size: 0.7rem;
  color: var(--muted-foreground);
}

.operation-table thead th:not(:first-child) {
  min-width: 250px;
}

.operation-table thead .mode-chip {
  margin-bottom: 0.25rem;
}

.operation-table tbody th {
  width: 9.5rem;
  color: var(--foreground);
}

.operation-table tbody td {
  color: var(--muted-foreground);
}

.operation-table tr:last-child th,
.operation-table tr:last-child td {
  border-bottom: 0;
}

.operator-notes {
  padding: 1rem 1.1rem;
  border-radius: 0.8rem;
  background: var(--muted);
}

.operator-notes ul {
  display: grid;
  gap: 0.5rem;
  margin: 0;
  padding-left: 1.1rem;
  font-size: 0.74rem;
  line-height: 1.55;
  color: var(--muted-foreground);
}

@media (max-width: 640px) {
  .billing-guide {
    padding-inline: 1rem;
  }

  .current-mode-strip,
  .timeline-comparison,
  .edge-case-grid {
    grid-template-columns: 1fr;
  }

  .section-heading {
    align-items: start;
    flex-direction: column;
    gap: 0.35rem;
  }

  .section-note {
    text-align: left;
  }

  .ratio-row {
    grid-template-columns: 1fr;
    gap: 0.45rem;
  }

  .ratio-row > p {
    grid-template-columns: auto auto auto auto;
    justify-content: start;
  }

}

@media (prefers-reduced-motion: reduce) {
  .billing-help-trigger {
    transition: none;
  }
}
</style>
